import {
  Badge,
  Button,
  Card,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { AdminApiError, adminErrText, adminSend } from "../api.ts";
import { AdminTable } from "../components/AdminTable.tsx";

interface RecentSpin {
  readonly createdAt: string;
  readonly botCode: string;
  readonly prizeName: string;
  readonly prizeType: string;
  readonly isJackpot: boolean;
}

interface MonitorSnapshot {
  readonly ts: string;
  readonly spinsLast5m: number;
  readonly spinsLast1h: number;
  readonly spinsLast24h: number;
  readonly newUsersLast24h: number;
  readonly activeBots: number;
  readonly recentSpins: ReadonlyArray<RecentSpin>;
}

type ConnState = "connected" | "reconnecting";

interface InviteClaimScanResult {
  readonly bots: number;
  readonly inviters: number;
  readonly grantedCount: number;
}

const METRIC_LABELS: Array<{
  key: keyof Omit<MonitorSnapshot, "ts" | "recentSpins">;
  label: string;
}> = [
  { key: "spinsLast5m", label: "近 5m 抽奖" },
  { key: "spinsLast1h", label: "近 1h 抽奖" },
  { key: "spinsLast24h", label: "近 24h 抽奖" },
  { key: "newUsersLast24h", label: "近 24h 新用户" },
  { key: "activeBots", label: "在线 Bot 数" },
];

export const MonitorPage = () => {
  const [snapshot, setSnapshot] = useState<MonitorSnapshot | null>(null);
  const [connState, setConnState] = useState<ConnState>("reconnecting");
  const [botFilter, setBotFilter] = useState<string | null>(null);

  const inviteClaimMut = useMutation({
    mutationFn: () => adminSend<InviteClaimScanResult>("POST", "/ops/invite-claim-scan"),
    onSuccess: (data) => {
      notifications.show({
        color: "green",
        title: "邀请首存复查完成",
        message: `扫描 ${data.bots} 个 Bot，共 ${data.inviters} 个邀请人，达成首存 ${data.grantedCount} 个。详情见服务器日志`,
        autoClose: 8000,
      });
    },
    onError: (e: AdminApiError) => {
      notifications.show({
        color: "red",
        title: "邀请首存复查失败",
        message: adminErrText(e.code, e.message),
      });
    },
  });

  useEffect(() => {
    const es = new EventSource("/api/v1/admin/monitor/stream", { withCredentials: true });

    es.addEventListener("snapshot", (e) => {
      try {
        const data = JSON.parse((e as MessageEvent<string>).data) as MonitorSnapshot;
        setSnapshot(data);
        setConnState("connected");
      } catch {
        // parse failure - keep old snapshot
      }
    });

    es.onerror = () => {
      setConnState("reconnecting");
    };

    return () => {
      es.close();
    };
  }, []);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>实时监控</Title>
        <Badge color={connState === "connected" ? "green" : "orange"} size="lg">
          {connState === "connected" ? "已连接" : "重连中"}
        </Badge>
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="md">
        {METRIC_LABELS.map(({ key, label }) => (
          <Card key={key} shadow="xs" padding="md" radius="md" withBorder>
            <Text size="xs" c="dimmed" mb={4}>
              {label}
            </Text>
            <Title order={3}>{snapshot !== null ? snapshot[key].toLocaleString() : "-"}</Title>
          </Card>
        ))}
      </SimpleGrid>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Text fw={600} size="lg" mb="md">
          手动触发后台任务
        </Text>
        <Text size="sm" c="dimmed" mb="md">
          用于排查问题时主动触发，不需等待 cron
          定时执行。执行结果摘要显示在通知栏，详情见服务器日志。
        </Text>
        <Group gap="md">
          <Button
            variant="light"
            color="blue"
            loading={inviteClaimMut.isPending}
            onClick={() => inviteClaimMut.mutate()}
          >
            复查邀请首存
          </Button>
        </Group>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Text fw={600} size="lg">
            最近中奖流水
          </Text>
          <Select
            size="xs"
            placeholder="按 Bot 过滤"
            clearable
            searchable
            data={Array.from(new Set((snapshot?.recentSpins ?? []).map((s) => s.botCode))).sort()}
            value={botFilter}
            onChange={setBotFilter}
          />
        </Group>
        {(() => {
          const spins = (snapshot?.recentSpins ?? []).filter(
            (s) => botFilter === null || s.botCode === botFilter,
          );
          return snapshot === null || spins.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" py="lg">
              暂无流水数据
            </Text>
          ) : (
            <AdminTable minWidth={500}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>时间</Table.Th>
                  <Table.Th>Bot</Table.Th>
                  <Table.Th>奖品</Table.Th>
                  <Table.Th>类型</Table.Th>
                  <Table.Th>大奖</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {spins.map((spin, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Text size="xs" style={{ whiteSpace: "nowrap" }}>
                        {new Date(spin.createdAt).toLocaleString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{spin.botCode}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{spin.prizeName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{spin.prizeType}</Text>
                    </Table.Td>
                    <Table.Td>
                      {spin.isJackpot ? (
                        <Badge color="yellow" size="sm">
                          大奖
                        </Badge>
                      ) : (
                        <Text size="xs" c="dimmed">
                          -
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </AdminTable>
          );
        })()}
      </Card>
    </Stack>
  );
};
